/**
 * Free Speech Flag utilities
 * Converts a byte sequence into an SVG flag with vertical RGB stripes.
 */

export interface FlagStripe {
  r: number;
  g: number;
  b: number;
}

export interface FlagData {
  stripes: FlagStripe[];
  remainder: number[]; // 0, 1, or 2 remaining bytes
  bytes: number[];
}

/** Parse a hex string like "09f9110299..." into an array of byte values */
export function hexToBytes(hex: string): number[] {
  // Remove spaces, 0x prefixes, colons
  const clean = hex.replace(/\s|0x|:/g, '').toLowerCase();
  if (!/^[0-9a-f]*$/.test(clean)) throw new Error('Invalid hex string');
  if (clean.length % 2 !== 0) throw new Error('Hex string must have even number of digits');
  const bytes: number[] = [];
  for (let i = 0; i < clean.length; i += 2) {
    bytes.push(parseInt(clean.slice(i, i + 2), 16));
  }
  return bytes;
}

/** Convert UTF-8 text to bytes */
export function textToBytes(text: string): number[] {
  return Array.from(new TextEncoder().encode(text));
}

/** Convert bytes to lowercase hex string */
export function bytesToHex(bytes: number[]): string {
  return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Convert bytes into FlagData (stripes + remainder) */
export function bytesToFlagData(bytes: number[]): FlagData {
  const stripes: FlagStripe[] = [];
  for (let i = 0; i + 2 < bytes.length; i += 3) {
    stripes.push({ r: bytes[i], g: bytes[i + 1], b: bytes[i + 2] });
  }
  const remainder = bytes.slice(Math.floor(bytes.length / 3) * 3);
  return { stripes, remainder, bytes };
}

/** Format remainder bytes as "+XX" or "+XX YY" for display */
export function formatRemainder(remainder: number[]): string {
  if (remainder.length === 0) return '';
  return '+' + remainder.map(b => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');
}

/** Luminance-based contrast check – returns 'white' or 'black' */
export function contrastColor(r: number, g: number, b: number): string {
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/** Generate an SVG string for the flag */
export function generateSVG(flagData: FlagData, width = 800, height = 500): string {
  const { stripes, remainder } = flagData;
  if (stripes.length === 0) return '';

  const stripeWidth = width / stripes.length;
  const remainderText = formatRemainder(remainder);

  const stripeSVGs = stripes.map((stripe, i) => {
    const x = i * stripeWidth;
    const color = `rgb(${stripe.r},${stripe.g},${stripe.b})`;
    return `  <rect x="${x.toFixed(2)}" y="0" width="${stripeWidth.toFixed(2)}" height="${height}" fill="${color}" />`;
  }).join('\n');

  // Remainder label in bottom-right of last stripe
  let remainderSVG = '';
  if (remainderText) {
    const lastStripe = stripes[stripes.length - 1];
    const textColor = contrastColor(lastStripe.r, lastStripe.g, lastStripe.b);
    const fontSize = Math.round(height * 0.14);
    const x = width - 16;
    const y = height - 20;
    remainderSVG = `  <text x="${x}" y="${y}" text-anchor="end" font-family="monospace" font-size="${fontSize}" fill="${textColor}" font-weight="bold">${remainderText}</text>`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
${stripeSVGs}
${remainderSVG}
</svg>`;
}

/** Generate a data: URI for the SVG (for img src) */
export function svgToDataUri(svg: string): string {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

/** Simple nanoid-like ID generator */
export function generateId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  for (const b of arr) id += chars[b % chars.length];
  return id;
}
